
import { NgForm } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { LoginService } from '../../login/login.service';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { Filter, FilterOperation } from 'src/app/core/jsonapi/filter';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { Company } from '../_types/company';
import { CompaniesService } from '../_services/companies.service';
import { ContractsService } from '../_services/contracts.service';
import { Contract } from '../_types/contract';
import { RxFormBuilder, RxFormGroup } from '@rxweb/reactive-form-validators';
import { Country } from '../../common/_types/country';
import { FormValidatorService } from 'src/app/core/services/form-validator.service';
import { CountriesService } from '../../common/_services/countries.service';
import { TransactionsSeriesService } from '../../transactions-series/_services/transactions-series.service';
import { TransactionSerie } from '../../transactions-series/_types/transaction-serie';
import { AddressesService } from '../../common/_services/addresses.service';
import { Address } from '../../common/_types/address';
import { CountriesRegionsService } from '../../common/_services/countries-regions.service';
import { CountryRegion } from '../../common/_types/country-region';
import { OverlayPanel } from 'primeng/overlaypanel';
import { CasesModesService } from '../../cases/_services/cases-modes.service';
import { CompanyCaseModeService } from '../_services/company-case-mode.service';
import { CompanyCaseMode } from '../_types/company-case-mode';

@Component({
    selector: 'app-companies-detail',
    templateUrl: './companies-detail.component.html',
    styleUrls: ['./companies-detail.component.css'],
    standalone: false
})
export class CompaniesDetailComponent implements OnInit {

  public companyFormGroup!: RxFormGroup;

  public company: Company = new Company();
  public transactionsSeries: TransactionSerie[] = [];
  public totalRecords: number = 0;
  public loading: boolean = false;
  public activeIndex: number = 1;
  public allowWrite: boolean = false;
  public allowDelete: boolean = false;
  public banks: any = [];
  public contracts: Array<Contract> = new Array<Contract>();
  public countries: Array<Country> = new Array<Country>();
  public countriesRegions: any = [];
  public caseModes: any[] = [];
  public selectedCaseModeIds: string[] = [];

  //panel de opciones de telefono
  @ViewChild('phoneOverlayPanel') public phoneOverlayPanel?:OverlayPanel;
  public phoneOverlayPanelData:any = {
    phoneNumber: null
  }
  
  constructor(
    private companiesService: CompaniesService,
    private addressesService: AddressesService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public loginService: LoginService,
    private route: ActivatedRoute,
    private router: Router,
    private topbarService: TopbarService,
    private contractsService: ContractsService,
    private formBuilder: RxFormBuilder,
    private formValidationService: FormValidatorService,
    private countriesService: CountriesService,
    private countriesRegionsService: CountriesRegionsService,
    private transactionsSeriesService: TransactionsSeriesService,
    private caseModesService: CasesModesService,
    private companyCaseModeService: CompanyCaseModeService
  ) {
    this.companyFormGroup = this.formBuilder.formGroup<Company>(this.company) as RxFormGroup;
  }

  ngOnInit(): void {
    this.topbarService.setTitle("Detalles de compañía");
    this.topbarService.addBreadcrumb("Compañías", "/companies");
    this.route.paramMap.subscribe((params: ParamMap) => {
      let id: string | null = params.get('id');
      if (id != null && id != '0') {
        this.loadCompany(id);
      }

      this.loadCountries();
      this.loadCountriesRegions();
      this.loadTransactionsSeries();
      this.loadCaseModes();

      //si no tiene address, la creamos
      if (this.company.address == null) this.company.address = new Address();
    });
    this.allowWrite = this.loginService.hasPermission("COMPANIES.WRITE");
    this.allowDelete = this.loginService.hasPermission("COMPANIES.DELETE");
  }

  async loadCompany(id: string) {
    this.company = await firstValueFrom(this.companiesService.get(id, { include: ['address'] }));
    this.companyFormGroup.patchValue(this.company);
    await this.loadCompanyCaseModes(id);
    this.loadContracts();
  }

  async loadTransactionsSeries() {
    let filters = {
      code: new Filter(FilterOperation.equals, 'transactionType.code', "", "outgoing_invoice")
    }

    let params: IParamsCollection = {
      filter: FilterBuilder.fromObject(filters).build()
    };

    let response = await firstValueFrom(this.transactionsSeriesService.all(params));
    this.transactionsSeries = response.data;
  }

  async loadContracts() {

    let filters = {
      companyId: new Filter(FilterOperation.equals, 'companyId', "", this.company.id)
    };

    let params: IParamsCollection = {
      filter: FilterBuilder.fromObject(filters).build()
    };

    let response = await firstValueFrom(this.contractsService.all(params));
    this.contracts = response.data;
  }

  async loadCountries() {
    let countryId = null;
    if (this.companyFormGroup.value.address?.countryRegionId != null) {
      let country = this.countriesRegions.find((m: any) => m.id == this.company.address?.countryRegionId);
      if (country != null) countryId = country.countryId;

    }

    let filters = {
      id: new Filter(FilterOperation.equals, 'id', "", countryId)
    };
    let params: IParamsCollection = {
      sort: ['name'],
    };
    params.filter = FilterBuilder.fromObject(filters).build();
    let response = await firstValueFrom(this.countriesService.all(params));
    this.countries = response.data;
  }

  async loadCountriesRegions() {
    let params: IParamsCollection = {
      sort: ['name']
    };
    let response = await firstValueFrom(this.countriesRegionsService.all(params));
    this.countriesRegions = response.data;
  }

  async loadCaseModes() {
    let params: IParamsCollection = {
      sort: ['name']
    };

    let response = await firstValueFrom(this.caseModesService.all(params));
    this.caseModes = response.data;
  }

  async loadCompanyCaseModes(companyId: string) {
    let filters = {
      companyId: new Filter(FilterOperation.equals, 'companyId', '', companyId)
    };

    let params: IParamsCollection = {
      filter: FilterBuilder.fromObject(filters).build()
    };

    let response = await firstValueFrom(this.companyCaseModeService.all(params));
    this.selectedCaseModeIds = response.data
      .map((item: CompanyCaseMode) => item.caseModeId)
      .filter((id: string | undefined) => !!id) as string[];
  }

  async saveCompanyCaseModes(companyId: string) {
    let filters = {
      companyId: new Filter(FilterOperation.equals, 'companyId', '', companyId)
    };

    let params: IParamsCollection = {
      filter: FilterBuilder.fromObject(filters).build()
    };

    let existingRelations = await firstValueFrom(this.companyCaseModeService.all(params));

    for (const relation of existingRelations.data) {
      await firstValueFrom(this.companyCaseModeService.delete(relation.id));
    }

    for (const caseModeId of this.selectedCaseModeIds) {
      let relation = new CompanyCaseMode();
      relation.companyId = companyId;
      relation.caseModeId = caseModeId;
      await firstValueFrom(this.companyCaseModeService.save(relation));
    }
  }


  async save() {
    let valid = this.formValidationService.validateForm(this.companyFormGroup);
    if (valid) {

      this.company = Object.assign(this.company, this.companyFormGroup.value);

      //guardamos direccion      
      if (this.company.address != null) {
        let address: any = await firstValueFrom(this.addressesService.save(this.company.address));
        this.company.addressId = address.id;
      }

      //guardamos company
      let company = await firstValueFrom(this.companiesService.save(this.company));
      await this.saveCompanyCaseModes(company.id!);


      if (this.company.id != "undefined" && this.company.id != "") {
        this.router.navigate(['companies']);
      }

      this.messageService.add({ closable: true, severity: 'success', summary: "Compañía aseguradora guardada correctamente." });

      this.loadCompany(company.id!);

    } else {
      this.messageService.add({ closable: true, severity: 'error', summary: "Error", detail: "Existen errores, por favor revise el formulario de datos." });
    }
  }

  async delete() {
    this.confirmationService.confirm({
      message: "¿Está seguro que desea eliminar la compañía aseguradora " + this.company.name + "?",
      header: "Eliminar compañía aseguradora",
      acceptLabel: "Aceptar",
      rejectLabel: "Cancelar",
      acceptButtonStyleClass: "p-button-danger",
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.companiesService.delete(this.company.id).subscribe({
          next: (data: any) => {
            this.cancel();
            this.messageService.add({ closable: true, severity: 'success', detail: "Compañía aseguradora eliminada correctamente." });
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
          }
        });
      },
      reject: (error: any) => {
        this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
      }
    });
  }

  cancel() {
    this.router.navigate(['companies']);
  }


  editContract(contract: any) {
    //if (this.loginService.hasPermission("COMPANIES_WRITE")) {      
    this.router.navigate(['companies/' + this.company.id + '/contracts/' + (contract && Object.keys(contract).length > 0 ? contract.id : 0)]);
    //}
  }

  deleteContract(contract: Contract) {
    if (this.allowDelete) {
      this.confirmationService.confirm({
        message: "¿Está seguro que desea eliminar el contrato " + contract.name + "?",
        header: "Eliminar contrato",
        acceptLabel: "Aceptar",
        rejectLabel: "Cancelar",
        acceptButtonStyleClass: "p-button-danger",
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.contractsService.delete(contract.id).subscribe({
            next: (data: any) => {
              this.messageService.add({ closable: true, severity: 'success', detail: "Contrato eliminado correctamente" });
              this.loadContracts();
            },
            error: (error: any) => {
              this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
            }
          });
        },
        reject: (error: any) => {
          this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
        }
      });
    }
  }

  showPhoneOverlayPanel($event:any, phoneNumber:string|undefined, callerId: string|undefined){
        this.phoneOverlayPanelData.phoneNumber = phoneNumber;
        this.phoneOverlayPanelData.callerId = callerId;
        this.phoneOverlayPanel?.show($event);
      }

}
